import * as React from 'react';
import * as I18n from 'i18n-js';
import { kebabCase, last } from 'lodash';

import SelectFacet from './select_facet';
import TextInputFacet from './text_input_facet';
import NestedSelectFacet from './nested_select_facet';
import RangeFacet from './range_facet';
import RegionSelectFilter from './region_select_filter';
import { Filter, WidgetType } from './search_filters';
import Popover from './popover';
import FilterChip from './filter_chip';
import { useUser } from '../user_hooks';
import { selectedPath } from '../facet_utils';
import { showSellerLocationFilter } from './seller_location_search_filter';
import { useURL } from '../url_context';

interface IProps {
  filters?: Filter[];
  loading?: boolean;
  trackingQuery?: string;
}

interface FilterProps {
  filter: Filter;
  trackingQuery?: string;
}

interface FacetMapping {
  [widgetType: string]: React.ComponentType<FilterProps>;
}

const FACET_MAPPING: FacetMapping = {
  [WidgetType.TEXT]: TextInputFacet,
  [WidgetType.NESTED_SELECT]: NestedSelectFacet,
  [WidgetType.CHECKBOX]: SelectFacet,
  [WidgetType.RADIO]: SelectFacet,
  [WidgetType.RANGE]: RangeFacet,
  [WidgetType.REGION_SELECT]: RegionSelectFilter,
  [WidgetType.NONE]: null,
  [WidgetType.SORT]: null,
};

const FACETS_WITH_ALL_FILTERS = ['CONDITION_SLUGS', 'CATEGORY_SLUGS', 'BRAND_SLUGS', 'DECADES'];

export const FEATURED = [
  'CONDITION_SLUGS',
  'CATEGORY_SLUGS',
  'BRAND_SLUGS',
  'PRICE',
  'FIND_A_DEAL',
];

export const MODEL_FAMILY_FEATURED = [
  'CONDITION_SLUGS',
  'PRICE',
  'FIND_A_DEAL',
];

export function excludedFilters(user, pathname) {
  const EXCLUDED = ['SORT_BY'];
  if (user && showSellerLocationFilter(user)) {
    EXCLUDED.push('SELLER_LOCATION');
  }
  if (pathname?.includes('/marketplace')) {
    EXCLUDED.push('TEXT_QUERY');
  }
  return EXCLUDED;
}

export default function MarketplaceFilterRow({
  filters = [],
  loading = false,
  trackingQuery = '',
}: IProps) {
  const user = useUser();
  const { pathname } = useURL();

  if (loading && filters.length === 0) {
    return (
      <>
        {FEATURED.map(f => {
          return <Placeholder key={f} />;
        })}
      </>
    );
  }

  if (filters.length === 0) return null;

  const featuredFilters = filters.filter(f => {
    const key = getFilterKey(f);
    if (pathname?.includes('/f/')) {
      return MODEL_FAMILY_FEATURED.includes(key);
    }
    return FEATURED.includes(key);
  });

  featuredFilters.sort(sortFeatured);

  let fallbackFilters = [];

  if (featuredFilters.length === 0) {
    fallbackFilters = filters.filter(f => {
      const key = getFilterKey(f);
      if (excludedFilters(user, pathname).includes(key)) return false;
      return true;
    }).slice(0, 5);
  }

  const fallbackFilterKeys = fallbackFilters.map(f => {
    return f.aggregationName ? f.aggregationName : f.key;
  });

  const otherSelectedFilters = filters.filter(f => {
    const key = getFilterKey(f);
    if (FEATURED.includes(key)) return false;
    if (excludedFilters(user, pathname).includes(key)) return false;
    if (fallbackFilterKeys.includes(key)) return false;
    return f.options && f.options.some(o => {
      if (o.all) return false;
      if (key === 'SHIPS_TO' && o.optionValue === user.shippingRegionCode) return false;
      return o.selected;
    });
  });

  return (
    <>
      {featuredFilters.map(f => {
        return (
          <MarketplaceFilter
            facet={f}
            loading={loading}
            key={f.name}
            trackingQuery={trackingQuery}
          />
        );
      })}
      {fallbackFilters.map(f => {
        return (
          <MarketplaceFilter
            facet={f}
            loading={loading}
            key={f.name}
            trackingQuery={trackingQuery}
          />
        );
      })}
      {otherSelectedFilters.map(f => {
        return (
          <MarketplaceFilter
            facet={f}
            loading={loading}
            key={f.name}
            trackingQuery={trackingQuery}
          />
        );
      })}
    </>
  );
}

function MarketplaceFilter({ facet, loading, trackingQuery }) {
  if (facet.options.length === 0) return null;
  const selectedFilters = facet.options?.filter(f => {
    return f.selected;
  });

  const isFilterSelected = (aggregationName) => {
    return facet.aggregationName === aggregationName && facet.options?.[0].selected === true;
  };

  const getButtonText = () => {
    if (facet.aggregationName === 'CATEGORY_SLUGS') {
      if (facet.options?.[0].selected === true) { return facet.name; }

      const category = selectedPath(facet);

      return last(category).name;
    }
    if (selectedFilters?.length === 0) return facet.name;

    if (isFilterSelected('CONDITION_SLUGS')) {
      return I18n.t('commons.marketplaceFilters.allConditions');
    }

    if (isFilterSelected('BRAND_SLUGS')) {
      return I18n.t('commons.marketplaceFilters.allBrands');
    }

    // Index 2 on a RANGE filter displays the combination of min & max values
    if (facet.widgetType === 'RANGE' && selectedFilters.length > 1) {
      return facet.options?.[2].name;
    }

    if (selectedFilters?.length > 1) return facet.name;
    return selectedFilters[0].name;
  };

  const getSelectedFilterCount = () => {
    // hides the count for range filters
    if (facet.widgetType === 'RANGE' && selectedFilters.length > 1) {
      return 1;
    }
    if (selectedFilters?.length !== 1) return selectedFilters.length;

    const hasAllFilter = FACETS_WITH_ALL_FILTERS.some(name => facet.aggregationName === name);
    if (hasAllFilter && facet.options?.[0].selected === true) {
      return 0;
    }
    return selectedFilters.length;
  };

  const FacetComponent = FACET_MAPPING[facet.widgetType];
  if (facet.options.length === 0 || !FacetComponent) return null;
  if (!getButtonText()) return null;

  return (
    <div className="mr-1 mobile-d-none">
      <Popover
        trigger={
          <FilterChip
            withDropdown
            selected={getSelectedFilterCount() > 0}
            count={getSelectedFilterCount()}
            loading={loading}
          >
            {getButtonText()}
          </FilterChip>
        }
        loading={loading}
      >
        <FacetComponent
          filter={facet}
          key={kebabCase(facet.name)}
          trackingQuery={trackingQuery}
        />
      </Popover>
    </div>
  );
}

function Placeholder() {
  return (
    <div className="mr-1">
      <FilterChip loading>
        <div className="filter-chip__loading-placeholder" />
      </FilterChip>
    </div>
  );
}

function getFilterKey(f: Filter) {
  return f.aggregationName || f.key;
}

function sortFeatured(a: Filter, b: Filter) {
  const keyA = getFilterKey(a);
  const keyB = getFilterKey(b);
  const indexOfA = FEATURED.indexOf(keyA);
  const indexOfB = FEATURED.indexOf(keyB);
  if (indexOfA < indexOfB) { return -1; }
  if (indexOfA > indexOfB) { return 1; }
  return 0;
}



// WEBPACK FOOTER //
// ./node_modules/@reverbdotcom/commons/src/components/marketplace_filter_row.tsx